/**
 * Platform events — wake lock, API performance and errors, cookie consent, clipboard.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const PLATFORM_EVENTS = {
  // Screen Wake Lock
  SCREEN_WAKE_LOCK_ACQUIRED: 'Screen Wake Lock Acquired',
  SCREEN_WAKE_LOCK_FAILED: 'Screen Wake Lock Failed',
  SCREEN_WAKE_LOCK_VIDEO_FALLBACK: 'Screen Wake Lock Video Fallback',

  // Performance / Errors
  API_REQUEST: 'API Request',
  API_ERROR_OCCURRED: 'API Error Occurred',
  /** A stale content-hashed chunk 404'd after a deploy and the page
   * reloaded to recover. `trigger: 'automatic'` is the one-shot guard
   * (lazyWithRetry / main.tsx net) and carries chunk_key + reload_count;
   * `trigger: 'manual'` is the user pressing Reload on the ErrorBoundary,
   * which only happens once that one shot is spent, and carries
   * boundary_level. Best-effort — may not flush before the reload. */
  STALE_CHUNK_RELOADED: 'Stale Chunk Reloaded',

  /** The app origin's Report-Only CSP blocked (or would block) a load
   * (#4771). `directive` is the effective directive, `blocked` and `source`
   * are origins or CSP keywords (`inline`, `eval`), never paths; `disposition`
   * is `report` until the policy is enforced. Stashed before init and flushed
   * (`utils/cspViolationTelemetry.ts`). */
  CSP_VIOLATION_REPORTED: 'CSP Violation Reported',

  /** The auth persistence-recovery backstop — see the docblock in
   * `utils/authPersistenceRecovery.ts`, which is where it lives.
   * `phase: 'reloading'` — Firebase resolved its persisted session to "no
   * user" on a page load for a device that still holds a workspace pin, so the
   * tab is reloading once to re-read persistence. `phase: 'exhausted'` — that
   * tab already spent its one reload and is falling through to the signed-out
   * path, i.e. the reload did NOT recover it.
   *
   * NOT sent where it is detected: that happens during Firebase's boot auth
   * resolve, before `safeAnalytics.init()` runs, so `track()` would drop it at
   * the `!isActive` gate. It is stashed and delivered by
   * `flushPendingAnalyticsEvents()` after init and on consent, the same
   * shape `emailCtaCapture` uses. Still best-effort: a consent-denied tab drops
   * it like any other event. The root cause
   * is deliberately unproven, and this event is the only evidence that could
   * ever retire the backstop — do not remove it while the backstop stands. */
  AUTH_PERSISTENCE_RECOVERY: 'Auth Persistence Recovery',
  /** A signup Turnstile failure: `attempt` 1 or 2 within a streak (later ones untracked). */
  SIGNUP_CHALLENGE_FAILED: 'Signup Challenge Failed',
  /** The verification gate still refused after the token refresh. Once per
   * account per page load, because every later request would repeat it.
   * `has_sso_identity` true should never happen: see the
   * auth-sso-identity-refused alert. */
  EMAIL_VERIFICATION_REFUSED: 'Email Verification Refused',
  BROWSER_EXTENSION_ERROR_SUPPRESSED: 'Browser Extension Error Suppressed',
  /** `services/logging.service.ts` gave up on frontend log entries: the
   * ingest rejected the batch (`reason: 'http_4xx'`), or it failed on every
   * one of its bounded attempts (`reason: 'network' | 'http_5xx'`, the last
   * failure seen). Counts only, never the entries themselves, which carry
   * messages and URLs. Sent here rather than through the log ingest, because
   * the ingest is the thing that is failing. */
  FRONTEND_LOGS_DROPPED: 'Frontend Logs Dropped',

  // Cookie Consent
  COOKIE_CONSENT_GIVEN: 'Cookie Consent Given',
  /** Analytics toggled OFF from Settings → Preferences → Privacy. Fired
   * through the normal pipeline immediately BEFORE the downgrade (the user
   * is still consented at that instant). */
  COOKIE_CONSENT_REVOKED: 'Cookie Consent Revoked',
  /** Analytics turned on WITHOUT a banner choice, because the
   * `analytics-region-default` flag placed this browser outside the opt-in
   * jurisdictions. Fired once, through the normal pipeline, immediately AFTER
   * the upgrade. Read against COOKIE_CONSENT_GIVEN to size how much of the
   * analytics population is defaulted rather than chosen. */
  COOKIE_CONSENT_DEFAULTED: 'Cookie Consent Defaulted',

  // Clipboard failures, shared by every hand-rolled copy affordance. ONE event
  // with a `surface` property rather than one per button: the question these
  // answer ("how often does a copy actually fail, and where") is the same
  // question at every site, and each success already has its own event.
  // Properties: surface — 'equipment_qr_link' | 'bird_table' | 'mcp_server_url'
  // | 'audit_event_id'; reason — 'unavailable' (no clipboard API, i.e. an
  // insecure context) | 'rejected' (the write itself was refused).
  CLIPBOARD_COPY_FAILED: 'Clipboard Copy Failed',
} as const;
